import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import session from "../services/session";
import { useNavigate } from "react-router-dom";
import ArrowUpwardIcon from "@material-ui/icons/ArrowUpward";
import ArrowDownwardIcon from "@material-ui/icons/ArrowDownward";
import "./Analytics.scss";
import {
  TimeSeriesChart,
  RankedBars,
  StatTile,
  formatNumber,
  formatMoney,
  STATUS,
} from "./analytics/charts";

const ADMIN_EMAILS = ["info.filmboard@gmail.com", "mishasund@gmail.com"];

const TABS = [
  "overview",
  "users",
  "billing",
  "projects",
  "assets",
  "engagement",
  "definitions",
];

const DAY = 24 * 60 * 60 * 1000;

const toISODate = (d) => new Date(d).toISOString().slice(0, 10);

const PRESETS = [
  { key: "7d", label: "7 days", days: 7 },
  { key: "30d", label: "30 days", days: 30 },
  { key: "90d", label: "90 days", days: 90 },
  { key: "12m", label: "12 months", days: 365 },
  { key: "all", label: "All time", days: null },
];

export default function Analytics() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");

  const [preset, setPreset] = useState("90d");
  const [from, setFrom] = useState(toISODate(Date.now() - 90 * DAY));
  const [to, setTo] = useState(toISODate(Date.now()));

  const [sortField, setSortField] = useState("date");
  const [sortDir, setSortDir] = useState("desc");

  const user = session.get("user");

  const applyPreset = (p) => {
    setPreset(p.key);
    const end = new Date();
    // "All time" simply reaches back before the product existed.
    const start = p.days ? new Date(end.getTime() - p.days * DAY) : new Date(2018, 0, 1);
    setFrom(toISODate(start));
    setTo(toISODate(end));
  };

  const fetchAnalytics = useCallback(
    async (rangeFrom, rangeTo) => {
      setLoading(true);
      setError(null);
      try {
        const res = await axios.post(
          `${process.env.REACT_APP_API_URL}/analytics`,
          { email: user.email, from: rangeFrom, to: rangeTo }
        );
        setData(res.data);
      } catch (err) {
        setError(
          err.response?.data || err.message || "Failed to load analytics."
        );
      }
      setLoading(false);
    },
    [user]
  );

  useEffect(() => {
    if (!user || !ADMIN_EMAILS.includes(user.email?.toLowerCase())) {
      navigate("/");
      return;
    }
    fetchAnalytics(from, to);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!user || !ADMIN_EMAILS.includes(user.email?.toLowerCase())) return;
    fetchAnalytics(from, to);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to]);

  const handleSort = (field) => {
    if (sortField === field) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else {
      setSortField(field);
      setSortDir("desc");
    }
  };

  const SortIcon = ({ field }) =>
    sortField !== field ? null : sortDir === "asc" ? (
      <ArrowUpwardIcon style={{ fontSize: "14px", marginLeft: "4px" }} />
    ) : (
      <ArrowDownwardIcon style={{ fontSize: "14px", marginLeft: "4px" }} />
    );

  const sortedProjects = useMemo(() => {
    if (!data?.projects?.list) return [];
    const rows = [...data.projects.list];
    const dir = sortDir === "asc" ? 1 : -1;
    return rows.sort((a, b) => {
      switch (sortField) {
        case "title":
          return dir * (a.title || "").localeCompare(b.title || "");
        case "creator":
          return dir * (a.creatorName || "").localeCompare(b.creatorName || "");
        case "date":
          return dir * (new Date(a.createdAt) - new Date(b.createdAt));
        case "views":
          return dir * ((a.views || 0) - (b.views || 0));
        case "assets":
          return dir * ((a.assetCount || 0) - (b.assetCount || 0));
        case "collaborators":
          return dir * ((a.collaboratorCount || 0) - (b.collaboratorCount || 0));
        default:
          return 0;
      }
    });
  }, [data, sortField, sortDir]);

  const def = (key) => data?.definitions?.[key]?.definition || "";

  if (loading && !data) {
    return (
      <div className="analytics-container">
        <div className="analytics-loading">Loading analytics…</div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="analytics-container">
        <div className="analytics-loading">Failed to load analytics: {String(error)}</div>
      </div>
    );
  }

  if (!data) return null;

  const { users, billing, projects, assets, engagement, dataQuality, range } = data;

  const rangeLabel = `${range.from.slice(0, 10)} → ${range.to.slice(0, 10)} · ${
    range.granularity
  } buckets · UTC`;

  // ---------------------------------------------------------------- controls
  const Controls = (
    <div className="analytics-controls">
      <div className="preset-group">
        {PRESETS.map((p) => (
          <button
            key={p.key}
            className={`preset-btn ${preset === p.key ? "active" : ""}`}
            onClick={() => applyPreset(p)}
          >
            {p.label}
          </button>
        ))}
      </div>
      <div className="date-group">
        <label>
          From
          <input
            type="date"
            value={from}
            max={to}
            onChange={(e) => {
              setPreset("custom");
              setFrom(e.target.value);
            }}
          />
        </label>
        <label>
          To
          <input
            type="date"
            value={to}
            min={from}
            onChange={(e) => {
              setPreset("custom");
              setTo(e.target.value);
            }}
          />
        </label>
      </div>
      <div className="range-note">{rangeLabel}</div>
    </div>
  );

  // --------------------------------------------------------- reconciliation
  const recon = billing.reconciliation;
  const billingUnavailable = !billing.available;

  const ReconciliationPanel = (
    <div
      className={`recon-panel ${
        billingUnavailable ? "recon-error" : recon.agrees ? "recon-ok" : "recon-warn"
      }`}
    >
      <div className="recon-head">
        <span
          className="recon-dot"
          style={{
            background: billingUnavailable
              ? STATUS.critical
              : recon.agrees
              ? STATUS.good
              : STATUS.warning,
          }}
        />
        <strong>
          {billingUnavailable
            ? "Billing not reconciled"
            : recon.agrees
            ? "Billing reconciles with Stripe"
            : "Billing does not reconcile with Stripe"}
        </strong>
      </div>

      {billingUnavailable ? (
        <p className="recon-body">
          {billing.reason} Figures below fall back to application state and should
          not be treated as revenue.
        </p>
      ) : (
        <>
          <p className="recon-body">
            Stripe reports <strong>{formatNumber(recon.paidPerStripe)}</strong> paying
            accounts and <strong>{formatMoney(billing.mrr)}</strong> MRR. The legacy
            application-state calculation reports{" "}
            <strong>{formatNumber(billing.appState.paidUsers)}</strong> paid users and{" "}
            <strong>{formatMoney(billing.appState.mrr)}</strong> from a hardcoded price
            table
            {recon.mrrDelta !== 0 && (
              <>
                {" "}
                — an overstatement of{" "}
                <strong style={{ color: STATUS.warning }}>
                  {formatMoney(Math.abs(recon.mrrDelta))}
                </strong>
              </>
            )}
            . Separately,{" "}
            <strong>{formatNumber(billing.appState.nonFreePackageUsers)}</strong>{" "}
            accounts sit on a non-free <code>package</code>. Every figure on this
            dashboard uses the Stripe number.
          </p>
          <div className="recon-grid">
            <div>
              <div
                className="recon-num"
                style={{ color: recon.overstatedBy ? STATUS.warning : STATUS.good }}
              >
                {formatNumber(recon.overstatedBy)}
              </div>
              <div className="recon-lbl">
                On a paid package, no active Stripe subscription
              </div>
            </div>
            <div>
              <div
                className="recon-num"
                style={{ color: recon.understatedBy ? STATUS.serious : STATUS.good }}
              >
                {formatNumber(recon.understatedBy)}
              </div>
              <div className="recon-lbl">
                Billed by Stripe, but the account sits on Free
              </div>
            </div>
            <div>
              <div
                className="recon-num"
                style={{
                  color: recon.orphanSubscriptions.length
                    ? STATUS.critical
                    : STATUS.good,
                }}
              >
                {formatNumber(recon.orphanSubscriptions.length)}
              </div>
              <div className="recon-lbl">
                Active Stripe subscriptions with no matching account
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );

  return (
    <div className="analytics-container">
      <div className="analytics-header">
        <div>
          <h2>Analytics Dashboard</h2>
          <div className="header-sub">
            Generated {new Date(data.generatedAt).toLocaleString()}
          </div>
        </div>
        <div className="analytics-tabs">
          {TABS.map((tab) => (
            <button
              key={tab}
              className={`analytics-tab ${activeTab === tab ? "active" : ""}`}
              onClick={() => setActiveTab(tab)}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {Controls}
      {loading && <div className="refresh-bar">Refreshing…</div>}

      {/* ------------------------------------------------------- OVERVIEW */}
      {activeTab === "overview" && (
        <>
          {ReconciliationPanel}

          <h3 className="section-title">
            All time <span className="section-hint">not affected by the date filter</span>
          </h3>
          <div className="analytics-cards">
            <StatTile
              value={formatNumber(users.total)}
              label="Total Users"
              definition={def("totalUsers")}
              sub="Accounts ever created"
            />
            <StatTile
              value={formatNumber(users.emailActivated)}
              label="Activated Accounts"
              definition={def("emailActivated")}
              sub={`${formatNumber(users.notEmailActivated)} never confirmed email`}
            />
            <StatTile
              value={formatNumber(projects.total)}
              label="Projects"
              definition={def("projectsCreated")}
              sub={`${formatNumber(projects.archived)} archived, excluded`}
            />
            <StatTile
              value={formatNumber(assets.total)}
              label="Total Assets"
              definition={def("totalAssets")}
              sub={`${formatNumber(assets.orphaned)} orphaned by deleted projects`}
            />
            <StatTile
              value={formatNumber(engagement.totalComments)}
              label="Comments"
              definition={def("comments")}
              sub={`+${formatNumber(engagement.totalReplies)} threaded replies`}
            />
          </div>

          <h3 className="section-title">
            Selected range <span className="section-hint">{rangeLabel}</span>
          </h3>
          <div className="analytics-cards">
            <StatTile
              value={formatNumber(users.activeUsers)}
              label="Active Users"
              definition={def("activeUsers")}
              tone="accent"
              emphasis
              sub="Created a project, asset, or comment"
            />
            <StatTile
              value={formatNumber(users.signupsInRange)}
              label="Signups"
              definition={def("signups")}
            />
            <StatTile
              value={formatNumber(projects.createdInRange)}
              label="Projects Created"
              definition={def("projectsCreated")}
            />
            <StatTile
              value={formatNumber(assets.createdInRange)}
              label="Assets Added"
              definition={def("totalAssets")}
            />
            <StatTile
              value={formatNumber(engagement.commentsInRange)}
              label="Comments Posted"
              definition={def("comments")}
            />
          </div>

          <h3 className="section-title">
            Billing <span className="section-hint">source of truth: Stripe</span>
          </h3>
          <div className="analytics-cards">
            <StatTile
              value={billing.available ? formatMoney(billing.mrr) : "n/a"}
              label="MRR"
              definition={def("mrr")}
              tone="accent"
              emphasis
              sub={billing.available ? `${formatMoney(billing.arr)} ARR` : billing.reason}
            />
            <StatTile
              value={billing.available ? formatNumber(billing.paidUsers) : "n/a"}
              label="Paid Users"
              definition={def("paidUsers")}
              sub={
                billing.available
                  ? `${formatNumber(billing.compedSubscriptions)} comped at $0`
                  : ""
              }
            />
            <StatTile
              value={billing.available ? formatMoney(billing.arpu) : "n/a"}
              label="ARPU"
              definition={def("arpu")}
            />
          </div>

          <div className="analytics-section">
            <h3>Signups over time</h3>
            <TimeSeriesChart
              data={users.signupSeries}
              granularity={range.granularity}
              label="signups"
            />
          </div>

          <div className="analytics-two-col">
            <div className="analytics-section">
              <h3>Projects created</h3>
              <TimeSeriesChart
                data={projects.series}
                granularity={range.granularity}
                label="projects"
                dense
              />
            </div>
            <div className="analytics-section">
              <h3>Assets added</h3>
              <TimeSeriesChart
                data={assets.series}
                granularity={range.granularity}
                label="assets"
                dense
              />
            </div>
          </div>
        </>
      )}

      {/* ---------------------------------------------------------- USERS */}
      {activeTab === "users" && (
        <>
          <div className="analytics-cards">
            <StatTile
              value={formatNumber(users.total)}
              label="Total Users"
              definition={def("totalUsers")}
              sub="All time"
            />
            <StatTile
              value={formatNumber(users.activeUsers)}
              label="Active Users"
              definition={def("activeUsers")}
              tone="accent"
              emphasis
              sub={`${
                users.total ? ((users.activeUsers / users.total) * 100).toFixed(1) : 0
              }% of all accounts`}
            />
            <StatTile
              value={formatNumber(users.emailActivated)}
              label="Activated Accounts"
              definition={def("emailActivated")}
              sub="Confirmed email — an onboarding state, not activity"
            />
            <StatTile
              value={formatNumber(users.signupsInRange)}
              label="Signups in Range"
              definition={def("signups")}
            />
          </div>

          <div className="callout">
            <strong>Total vs Active.</strong> {formatNumber(users.total)} accounts
            exist; {formatNumber(users.activeUsers)} of them did something in this
            range. The previous dashboard labelled the email-confirmation flag
            "Active Users", which is an onboarding state and never decreases — it is
            shown here as "Activated Accounts" instead.
          </div>

          <div className="analytics-section">
            <h3>Signups over time</h3>
            <TimeSeriesChart
              data={users.signupSeries}
              granularity={range.granularity}
              label="signups"
            />
          </div>

          <div className="analytics-two-col">
            <div className="analytics-section">
              <h3>Users by package</h3>
              <RankedBars
                rows={users.byPackage.map((p) => ({
                  label: p.name,
                  count: p.count,
                }))}
                note="Application state. Compare against the Billing tab before trusting paid tiers."
              />
            </div>
            <div className="analytics-section">
              <h3>How active users were counted</h3>
              <RankedBars
                rows={[
                  { label: "Created a project", count: users.createdProject },
                  { label: "Added assets", count: users.addedAssets },
                  { label: "Commented", count: users.commented },
                ]}
                note="Buckets overlap; the Active Users figure is the distinct union."
              />
            </div>
          </div>
        </>
      )}

      {/* -------------------------------------------------------- BILLING */}
      {activeTab === "billing" && (
        <>
          {ReconciliationPanel}

          {billing.available && (
            <>
              <div className="analytics-cards">
                <StatTile
                  value={formatMoney(billing.mrr)}
                  label="MRR (Stripe)"
                  definition={def("mrr")}
                  tone="accent"
                  emphasis
                />
                <StatTile
                  value={formatMoney(billing.arr)}
                  label="ARR"
                  definition={def("arr")}
                />
                <StatTile
                  value={formatNumber(billing.paidUsers)}
                  label="Paid Users"
                  definition={def("paidUsers")}
                />
                <StatTile
                  value={formatMoney(billing.arpu)}
                  label="ARPU"
                  definition={def("arpu")}
                />
                <StatTile
                  value={formatNumber(billing.compedSubscriptions)}
                  label="Comped ($0)"
                  definition="Active subscriptions whose price resolves to $0 — real customers contributing no revenue."
                />
                <StatTile
                  value={formatNumber(billing.newSubscriptionsInRange)}
                  label="New Subs in Range"
                  definition="Stripe subscriptions whose created date falls inside the selected range."
                />
                <StatTile
                  value={formatNumber(billing.canceledSubscriptionsInRange)}
                  label="Cancelled in Range"
                  definition="Stripe subscriptions whose canceled_at falls inside the selected range."
                />
              </div>

              <div className="analytics-two-col">
                <div className="analytics-section">
                  <h3>Subscriptions by status</h3>
                  <RankedBars
                    rows={Object.entries(billing.subscriptionsByStatus)
                      .map(([k, v]) => ({ label: k.replace(/_/g, " "), count: v }))
                      .sort((a, b) => b.count - a.count)}
                    note="Straight from Stripe, all time."
                    capitalizeLabels
                  />
                </div>
                <div className="analytics-section">
                  <h3>MRR by price</h3>
                  <div className="analytics-table">
                    <div className="table-header table-4col">
                      <div>Price</div>
                      <div>Interval</div>
                      <div>Subs</div>
                      <div>MRR</div>
                    </div>
                    {billing.byPrice.map((p) => (
                      <div className="table-row table-4col" key={p.priceId}>
                        <div>
                          {p.nickname ||
                            (p.unitAmount != null
                              ? formatMoney(p.unitAmount / 100)
                              : "—")}
                          <div className="text-muted small">{p.priceId}</div>
                        </div>
                        <div>{p.interval || "—"}</div>
                        <div>{p.subscriptions}</div>
                        <div>{formatMoney(p.mrr)}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {recon.appOnly.length > 0 && (
                <div className="analytics-section">
                  <h3>
                    On a paid package with no active Stripe subscription (
                    {recon.appOnly.length})
                  </h3>
                  <p className="section-hint block">
                    These accounts inflate the application's own revenue figure.
                    Each keeps paid features without being billed.
                  </p>
                  <div className="analytics-table">
                    <div className="table-header table-4col">
                      <div>Name</div>
                      <div>Email</div>
                      <div>Package</div>
                      <div>Why</div>
                    </div>
                    {recon.appOnly.map((u) => (
                      <div className="table-row table-4col" key={u._id}>
                        <div>{u.fullname || "—"}</div>
                        <div className="text-muted">{u.email}</div>
                        <div>{u.packageName}</div>
                        <div className="text-muted small">{u.reason}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {recon.stripeOnly.length > 0 && (
                <div className="analytics-section">
                  <h3>
                    Billed by Stripe but on the Free package ({recon.stripeOnly.length})
                  </h3>
                  <p className="section-hint block">
                    These accounts are paying but may not have the features they
                    bought.
                  </p>
                  <div className="analytics-table">
                    <div className="table-header table-3col">
                      <div>Name</div>
                      <div>Email</div>
                      <div>Subscription</div>
                    </div>
                    {recon.stripeOnly.map((u) => (
                      <div className="table-row table-3col" key={u._id}>
                        <div>{u.fullname || "—"}</div>
                        <div className="text-muted">{u.email}</div>
                        <div className="text-muted small">
                          {u.stripeSubscriptionId || "matched by email"}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {billing.unpriceableSubscriptions.length > 0 && (
                <div className="callout">
                  <strong>
                    {billing.unpriceableSubscriptions.length} subscription(s)
                    could not be valued
                  </strong>{" "}
                  — metered or tiered pricing has no flat unit amount, so they are
                  excluded from MRR rather than guessed at:{" "}
                  {billing.unpriceableSubscriptions.join(", ")}
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* ------------------------------------------------------- PROJECTS */}
      {activeTab === "projects" && (
        <>
          <div className="analytics-cards">
            <StatTile
              value={formatNumber(projects.total)}
              label="Projects"
              definition={def("projectsCreated")}
              sub="Active, excluding demo templates"
            />
            <StatTile
              value={formatNumber(projects.createdInRange)}
              label="Created in Range"
              definition={def("projectsCreated")}
            />
            <StatTile
              value={formatNumber(projects.archived)}
              label="Archived"
              definition="Projects with active=false. Excluded from every other project figure."
            />
            <StatTile
              value={formatNumber(engagement.projectsWithCollaborators)}
              label="With Collaborators"
              definition="Distinct projects that have at least one collaboration invite."
            />
          </div>

          <div className="analytics-section">
            <h3>Projects created over time</h3>
            <TimeSeriesChart
              data={projects.series}
              granularity={range.granularity}
              label="projects"
            />
          </div>

          <div className="analytics-section">
            <h3>
              Projects <span className="section-hint">newest 1000</span>
            </h3>
            <div className="analytics-table">
              <div className="table-header table-6col">
                {[
                  ["title", "Project"],
                  ["creator", "Created By"],
                  ["date", "Created"],
                  ["views", "Views"],
                  ["assets", "Assets"],
                  ["collaborators", "Collaborators"],
                ].map(([f, l]) => (
                  <div key={f} className="sortable-header" onClick={() => handleSort(f)}>
                    {l} <SortIcon field={f} />
                  </div>
                ))}
              </div>
              {sortedProjects.map((p) => (
                <div key={p._id} className="table-row table-6col">
                  <div>
                    <a
                      href={`https://app.filmboard.io/n/${p._id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="project-link"
                    >
                      {p.title || "Untitled"}
                    </a>
                  </div>
                  <div>
                    <div>{p.creatorName || "—"}</div>
                    <div className="text-muted small">{p.creatorEmail}</div>
                  </div>
                  <div>{new Date(p.createdAt).toLocaleDateString()}</div>
                  <div>{formatNumber(p.views || 0)}</div>
                  <div>{formatNumber(p.assetCount)}</div>
                  <div>
                    {p.collaboratorCount > 0 ? (
                      <div>
                        <span>{p.collaboratorCount}</span>
                        <div className="collab-emails">
                          {p.collaboratorEmails.map((email, i) => (
                            <div key={i} className="text-muted small">
                              {email}
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <span className="text-muted">None</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* --------------------------------------------------------- ASSETS */}
      {activeTab === "assets" && (
        <>
          <div className="analytics-cards">
            <StatTile
              value={formatNumber(assets.total)}
              label="Total Assets"
              definition={def("totalAssets")}
              sub="All time, all projects"
            />
            <StatTile
              value={formatNumber(assets.createdInRange)}
              label="Added in Range"
              definition={def("totalAssets")}
            />
            <StatTile
              value={formatNumber(assets.orphaned)}
              label="Orphaned"
              definition="Assets whose parent project no longer exists. They still count toward Total Assets."
              tone="warn"
            />
          </div>

          <div className="analytics-section">
            <h3>Assets added over time</h3>
            <TimeSeriesChart
              data={assets.series}
              granularity={range.granularity}
              label="assets"
            />
          </div>

          <div className="analytics-two-col">
            <div className="analytics-section">
              <h3>By source</h3>
              <RankedBars
                rows={assets.bySource.map((s) => ({
                  label: s.source,
                  count: s.count,
                  sub:
                    s.inferred > 0
                      ? `${formatNumber(s.inferred)} inferred from file URL`
                      : null,
                }))}
                note="Where the source column is empty the host is inferred from the file URL, so uploads and embeds are not lumped into one Unknown bucket."
                capitalizeLabels
              />
            </div>
            <div className="analytics-section">
              <h3>By type</h3>
              <RankedBars
                rows={assets.byType.map((t) => ({ label: t.type, count: t.count }))}
                capitalizeLabels
              />
            </div>
          </div>

          <div className="analytics-section">
            <h3>Type × source</h3>
            <div className="analytics-table">
              <div className="table-header table-3col">
                <div>Type</div>
                <div>Source</div>
                <div>Count</div>
              </div>
              {assets.byTypeAndSource.map((m) => (
                <div className="table-row table-3col" key={`${m.type}-${m.source}`}>
                  <div className="capitalize">{m.type}</div>
                  <div className="capitalize">{m.source}</div>
                  <div>{formatNumber(m.count)}</div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* ----------------------------------------------------- ENGAGEMENT */}
      {activeTab === "engagement" && (
        <>
          <div className="analytics-cards">
            <StatTile
              value={formatNumber(engagement.totalComments)}
              label="Comments"
              definition={def("comments")}
              sub="All time"
            />
            <StatTile
              value={formatNumber(engagement.commentsInRange)}
              label="Comments in Range"
              definition={def("comments")}
            />
            <StatTile
              value={formatNumber(engagement.totalReplies)}
              label="Threaded Replies"
              definition="Replies nested inside comment documents. Counted separately from top-level comments."
            />
            <StatTile
              value={formatNumber(engagement.totalCollaborations)}
              label="Collaboration Invites"
              definition={def("collaborations")}
              sub="All time"
            />
            <StatTile
              value={formatNumber(engagement.acceptedCollaborations)}
              label="Accepted"
              definition={def("collaborations")}
              sub={`${engagement.collaborationAcceptanceRate}% acceptance rate`}
            />
            <StatTile
              value={formatNumber(engagement.projectsWithCollaborators)}
              label="Shared Projects"
              definition="Distinct projects with at least one collaboration invite."
            />
          </div>

          <div className="analytics-two-col">
            <div className="analytics-section">
              <h3>Comments over time</h3>
              <TimeSeriesChart
                data={engagement.commentSeries}
                granularity={range.granularity}
                label="comments"
                dense
              />
            </div>
            <div className="analytics-section">
              <h3>Collaboration invites over time</h3>
              <TimeSeriesChart
                data={engagement.collaborationSeries}
                granularity={range.granularity}
                label="invites"
                dense
              />
            </div>
          </div>
        </>
      )}

      {/* ---------------------------------------------------- DEFINITIONS */}
      {activeTab === "definitions" && (
        <>
          <div className="analytics-section">
            <h3>KPI definitions</h3>
            <p className="section-hint block">
              Every figure on this dashboard, and exactly how it is calculated.
              All time bucketing is UTC.
            </p>
            <div className="analytics-table">
              <div className="table-header table-def">
                <div>KPI</div>
                <div>Definition</div>
                <div>Source</div>
              </div>
              {Object.entries(data.definitions).map(([key, d]) => (
                <div className="table-row table-def" key={key}>
                  <div>
                    <strong>{d.label}</strong>
                  </div>
                  <div>{d.definition}</div>
                  <div className="text-muted small">
                    <code>{d.source}</code>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="analytics-section">
            <h3>Data quality</h3>
            <p className="section-hint block">{dataQuality.note}</p>
            <div className="analytics-table">
              <div className="table-header table-5col">
                <div>Collection</div>
                <div>Date column</div>
                <div>Documents</div>
                <div>Drifted &gt; 1 day</div>
                <div>Share</div>
              </div>
              {["users", "projects", "assets", "comments", "collaborations"].map(
                (k) => {
                  const q = dataQuality[k];
                  const bad = q.driftPct > 10;
                  return (
                    <div className="table-row table-5col" key={k}>
                      <div className="capitalize">{k}</div>
                      <div>
                        <code>{q.field}</code>
                      </div>
                      <div>{formatNumber(q.total)}</div>
                      <div>{formatNumber(q.driftedOverADay)}</div>
                      <div
                        style={{
                          color: bad ? STATUS.warning : STATUS.good,
                          fontWeight: 600,
                        }}
                      >
                        {q.driftPct}%
                      </div>
                    </div>
                  );
                }
              )}
            </div>
            <div className="callout" style={{ marginTop: "16px" }}>
              <strong>Why this matters.</strong> These date columns were declared
              with <code>default: Date.now()</code> — evaluated once when the
              server process started, so every record written by that process
              shared a single timestamp. The schema defaults are fixed, but the
              historical rows above are still wrong, which is why every chart here
              buckets on the record's own creation time instead.
            </div>
          </div>
        </>
      )}
    </div>
  );
}
